import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Stack,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  Visibility,
  Edit,
  Add,
  EventRepeat,
  PostAdd,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  getActivities,
  getActivityTypeOptions,
  getPriorityOptions,
  getStatusOptions,
  getSeasonOptions,
} from "../../api/planning";
import { useSnackbar } from "notistack";
import { format } from "date-fns";
import useAuth from "../../hooks/useAuth";
import useIsMobileList from "../../hooks/useIsMobileList";
import MobileListCard from "../../components/MobileListCard";
// import { CalendarTodayOutlined } from "@mui/icons-material";

function ActivityCard({
  activity,
  planningSearchSuffix,
  getPriorityColor,
  getStatusColor,
}) {
  const dateLabel = format(new Date(activity.start_date), "MMM dd, yyyy");
  const endLabel =
    !activity.is_all_day && activity.end_date
      ? ` to ${format(new Date(activity.end_date), "MMM dd, yyyy")}`
      : "";

  return (
    <MobileListCard
      title={activity.name}
      subtitle={activity.activity_type}
      status={
        <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="flex-end">
          <Chip
            label={activity.priority}
            color={getPriorityColor(activity.priority)}
            size="small"
          />
          <Chip
            label={activity.status}
            color={getStatusColor(activity.status)}
            size="small"
          />
        </Stack>
      }
      fields={[
        {
          label: "Dates",
          value: `${dateLabel}${endLabel}`,
        },
        { label: "Season", value: activity.season || "—" },
        ...(activity.description
          ? [
              {
                label: "Description",
                value:
                  activity.description.length > 80
                    ? `${activity.description.substring(0, 80)}...`
                    : activity.description,
              },
            ]
          : []),
      ]}
      footer={
        activity.is_recurring ? (
          <Tooltip title="Recurring Activity">
            <EventRepeat color="action" fontSize="small" />
          </Tooltip>
        ) : null
      }
      actions={
        <>
          <Tooltip title="View Details">
            <IconButton
              component={RouterLink}
              to={`/planning/activities/${activity.id}${planningSearchSuffix}`}
              color="primary"
              size="small"
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              component={RouterLink}
              to={`/planning/activities/${activity.id}/edit${planningSearchSuffix}`}
              color="secondary"
              size="small"
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </>
      }
    />
  );
}

const ActivityList = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isSuperAdmin = Boolean(
    user?.roles?.some((r) => r === "super_admin" || r?.name === "super_admin")
  );
  const farmTenantParam = useMemo(() => {
    if (!isSuperAdmin) return null;
    const raw = searchParams.get("tenant_id");
    if (raw == null || raw === "") return null;
    const tid = Number(raw);
    return Number.isNaN(tid) ? null : tid;
  }, [isSuperAdmin, searchParams]);

  const planningSearchSuffix = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    activity_type: "",
    priority: "",
    status: "",
    season: "",
    search: "",
    start_date: null,
    end_date: null,
  });
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobileList("md");

  useEffect(() => {
    const fetchActivities = async () => {
      if (isSuperAdmin && farmTenantParam == null) {
        setActivities([]);
        setLoading(false);
        return;
      }
      try {
        const params = {};
        if (filters.activity_type) params.activity_type = filters.activity_type;
        if (filters.priority) params.priority = filters.priority;
        if (filters.status) params.status = filters.status;
        if (filters.season) params.season = filters.season;
        if (filters.search) params.search = filters.search;
        if (filters.start_date)
          params.start_date = format(filters.start_date, "yyyy-MM-dd");
        if (filters.end_date)
          params.end_date = format(filters.end_date, "yyyy-MM-dd");

        const data = await getActivities(params, {
          tenantId: farmTenantParam,
        });
        setActivities(data);
      } catch (err) {
        setError(err.message || "Failed to fetch activities");
        enqueueSnackbar("Failed to load activities", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [filters, enqueueSnackbar, isSuperAdmin, farmTenantParam]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Planned":
        return "primary";
      case "In Progress":
        return "warning";
      case "Completed":
        return "success";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Low":
        return "success";
      case "Medium":
        return "warning";
      case "High":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {isSuperAdmin && farmTenantParam == null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Add{" "}
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            ?tenant_id=(farm id)
          </Box>{" "}
          to this address bar to load activities for an organization.
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4">Farm Activities</Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ flexShrink: 0 }}
        >
          <Button
            variant="contained"
            component={RouterLink}
            to="/planning/calendar"
          >
            Calendar View
          </Button>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to={`/planning/activities/new${planningSearchSuffix}`}
            startIcon={<Add />}
          >
            New Activity
          </Button>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to="/planning/templates"
            startIcon={<PostAdd />}
          >
            Templates
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={filters.activity_type}
                label="Activity Type"
                onChange={(e) =>
                  handleFilterChange("activity_type", e.target.value)
                }
              >
                <MenuItem value="">All Types</MenuItem>
                {getActivityTypeOptions().map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                label="Priority"
                onChange={(e) => handleFilterChange("priority", e.target.value)}
              >
                <MenuItem value="">All Priorities</MenuItem>
                {getPriorityOptions().map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {getStatusOptions().map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Season</InputLabel>
              <Select
                value={filters.season}
                label="Season"
                onChange={(e) => handleFilterChange("season", e.target.value)}
              >
                <MenuItem value="">All Seasons</MenuItem>
                {getSeasonOptions().map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="From Date"
              value={filters.start_date}
              onChange={(date) => handleFilterChange("start_date", date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="To Date"
              value={filters.end_date}
              onChange={(date) => handleFilterChange("end_date", date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              onClick={() =>
                setFilters({
                  activity_type: "",
                  priority: "",
                  status: "",
                  season: "",
                  search: "",
                  start_date: null,
                  end_date: null,
                })
              }
              fullWidth
              sx={{ height: "56px" }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {isMobile ? (
        <Stack spacing={1.5}>
          {activities.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No activities found
            </Typography>
          ) : (
            activities.map((activity) => (
              <ActivityCard
                key={activity.id}
                activity={activity}
                planningSearchSuffix={planningSearchSuffix}
                getPriorityColor={getPriorityColor}
                getStatusColor={getStatusColor}
              />
            ))
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                <TableCell sx={{ color: "white" }}>Name</TableCell>
                <TableCell sx={{ color: "white" }}>Type</TableCell>
                <TableCell sx={{ color: "white" }}>Dates</TableCell>
                <TableCell sx={{ color: "white" }}>Priority</TableCell>
                <TableCell sx={{ color: "white" }}>Status</TableCell>
                <TableCell sx={{ color: "white" }}>Season</TableCell>
                <TableCell sx={{ color: "white" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((activity) => (
                <TableRow key={activity.id}>
                  <TableCell>
                    <Typography fontWeight="medium">{activity.name}</Typography>
                    {activity.description && (
                      <Typography variant="body2" color="text.secondary">
                        {activity.description.length > 50
                          ? `${activity.description.substring(0, 50)}...`
                          : activity.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{activity.activity_type}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {format(new Date(activity.start_date), "MMM dd, yyyy")}
                      </Typography>
                      {!activity.is_all_day && activity.end_date && (
                        <Typography variant="body2">
                          to {format(new Date(activity.end_date), "MMM dd, yyyy")}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activity.priority}
                      color={getPriorityColor(activity.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activity.status}
                      color={getStatusColor(activity.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{activity.season}</TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        component={RouterLink}
                        to={`/planning/activities/${activity.id}${planningSearchSuffix}`}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        component={RouterLink}
                        to={`/planning/activities/${activity.id}/edit${planningSearchSuffix}`}
                        color="secondary"
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    {activity.is_recurring && (
                      <Tooltip title="Recurring Activity">
                        <EventRepeat color="action" />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {activities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No activities found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ActivityList;
